import React, { ReactNode } from 'react';

import { ReactComponent as QuoteSvg } from '../images/quote.svg';
import type { Theme } from '../../types';

import {
  QuoteSummary,
  QuoteBody,
  ImageRow,
  MobileImageContainer,
  DesktopImageContainer,
  AfterLabel,
  BeforeLabel,
  Quotation,
  InfoContainer,
  QuoteBodyContainer,
  BeforeAfterCaseContainer,
  ClickableLogo,
  Disclaimer,
  GoalsContainer,
  SkinGoal,
  GoalHeadline,
} from './BeforeAfterCase.style';
import { navInternalWithQueryString } from 'utils/marketingSafeDomain';

export interface BeforeAfterCaseProps {
  summary: string;
  partner?: boolean;
  quote?: ReactNode;
  clickableLogo?: ReactNode;
  clickableLogoLink?: string;
  beforeLabel?: string;
  afterLabel: string;
  beforeImageMobile: ReactNode;
  beforeImageDesktop: ReactNode;
  afterImageMobile: ReactNode;
  afterImageDesktop: ReactNode;
  theme?: Theme;
  skinGoals?: string[];
  imageOnly?: boolean;
}

export const BeforeAfterCase = ({
  summary,
  partner,
  quote,
  clickableLogo,
  clickableLogoLink,
  beforeLabel = 'Before',
  afterLabel,
  beforeImageMobile,
  beforeImageDesktop,
  afterImageMobile,
  afterImageDesktop,
  theme = 'primary',
  skinGoals,
  imageOnly = false,
}: BeforeAfterCaseProps) => {
  const disclaimerText = partner
    ? 'Curology partner. Results may vary.'
    : 'Results may vary';

  return (
    <BeforeAfterCaseContainer imageOnly={imageOnly}>
      <ImageRow>
        <DesktopImageContainer>
          {beforeImageDesktop}
          <BeforeLabel>{beforeLabel}</BeforeLabel>
        </DesktopImageContainer>
        <DesktopImageContainer>
          {afterImageDesktop}
          <AfterLabel>{afterLabel}</AfterLabel>
        </DesktopImageContainer>
        <MobileImageContainer>
          {beforeImageMobile}
          <BeforeLabel>{beforeLabel}</BeforeLabel>
        </MobileImageContainer>
        <MobileImageContainer>
          {afterImageMobile}
          <AfterLabel>{afterLabel}</AfterLabel>
        </MobileImageContainer>
      </ImageRow>
      {!imageOnly && (
        <React.Fragment>
          {theme !== 'tertiary' && <Disclaimer>{disclaimerText}</Disclaimer>}
          <InfoContainer themeType={theme}>
            <QuoteBodyContainer>
              {theme !== 'tertiary' && (
                <Quotation>
                  <QuoteSvg />
                </Quotation>
              )}
              <QuoteSummary>{summary}</QuoteSummary>
              {quote && <QuoteBody>{quote}</QuoteBody>}
              {clickableLogo && (
                <ClickableLogo
                  onClick={() => navInternalWithQueryString(clickableLogoLink)}
                >
                  {clickableLogo}
                </ClickableLogo>
              )}
            </QuoteBodyContainer>
            {skinGoals && (
              <div>
                <GoalHeadline>Skin Goals</GoalHeadline>
                <GoalsContainer>
                  {skinGoals.map((goal, index) => (
                    <SkinGoal key={index}>{goal}</SkinGoal>
                  ))}
                </GoalsContainer>
              </div>
            )}
          </InfoContainer>
        </React.Fragment>
      )}
    </BeforeAfterCaseContainer>
  );
};
